// src/utils/pushNotifications.js
// Registers the device for APNs push notifications and saves the token to
// Supabase so the server can send push notifications to this device.
//
// Call registerPushNotifications(supabase, userId) once after the user logs in,
// on native iOS only.
//
// Push payload data shape (fields spread alongside `aps` in the APNs body):
//   screen    — target screen: "people" | "home" | "inbox" | "schedule" | "calendars"
//   person_id — UUID; open that person's drawer when screen === "people"
//   notif_id  — UUID; scroll to that inbox item when screen === "inbox"
//   event_key — e.g. "birthday_reminder"; used as fallback when `screen` is absent

import { Capacitor } from '@capacitor/core';
import apiFetch from '../apiFetch.js';

// Map event_key → { screen, usesPersonId }
const EVENT_KEY_SCREEN = {
  birthday_reminder:    { screen: 'people', usesPersonId: true },
  anniversary_reminder: { screen: 'people', usesPersonId: true },
  anchor_reminder:      { screen: 'people', usesPersonId: true },
  connection_drift:     { screen: 'people', usesPersonId: true },
  fairness_drift:       { screen: 'home',   usesPersonId: false },
  new_connection:       { screen: 'inbox',  usesPersonId: false },
  schedule_received:    { screen: 'inbox',  usesPersonId: false },
  schedule_confirmed:   { screen: 'inbox',  usesPersonId: false },
  schedule_declined:    { screen: 'inbox',  usesPersonId: false },
  schedule_cancelled:   { screen: 'inbox',  usesPersonId: false },
  counter_proposed:     { screen: 'inbox',  usesPersonId: false },
};

/**
 * Request push permission, get the APNs device token, and upsert it into
 * the `push_tokens` table.
 *
 * No-op on web.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} userId
 */
export async function registerPushNotifications(supabase, userId) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('[push] Permission not granted');
      return;
    }

    // Register with APNs
    await PushNotifications.register();

    // Listen for the device token
    const regHandle = await PushNotifications.addListener('registration', async ({ value: deviceToken }) => {
      regHandle.remove();
      // Upsert the device token; on conflict (same user + token) update updated_at
      const { error } = await supabase
        .from('push_tokens')
        .upsert(
          { user_id: userId, device_token: deviceToken, platform: 'ios', updated_at: new Date().toISOString() },
          { onConflict: 'user_id,device_token' }
        );
      if (error) console.error('[push] Failed to save device token:', error.message);
      else {
        // Store so we can clean up on logout
        try { localStorage.setItem('orbit_push_token', deviceToken); } catch {}
      }
    });

    // Handle registration errors
    const errHandle = await PushNotifications.addListener('registrationError', ({ error }) => {
      errHandle.remove();
      console.error('[push] Registration error:', error);
    });

    // Handle foreground notifications (show as in-app banner)
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[push] Received foreground notification:', notification.title);
    });

    // Clear badge when app is already open and user is actively using it
    const { App } = await import('@capacitor/app');
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) PushNotifications.removeAllDeliveredNotifications().catch(() => {});
    });

    // Handle tap on a notification — navigate to the right screen
    await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const data = notification.data || {};

      // Resolve target screen: explicit `screen` field takes priority, then event_key lookup
      const meta = data.event_key ? EVENT_KEY_SCREEN[data.event_key] : null;
      const screen = data.screen || meta?.screen || 'home';
      const personId = (data.person_id && (data.screen === 'people' || (!data.screen && meta?.usesPersonId)))
        ? data.person_id
        : null;
      const notifId = data.notif_id || null;

      window.dispatchEvent(new CustomEvent('push:navigate', {
        detail: { screen, personId, notifId },
      }));
    });

  } catch (err) {
    console.error('[push] Setup failed:', err);
  }
}

/**
 * Removes the stored APNs device token from the server. Call before signOut()
 * while the session is still active so the Bearer token can be sent.
 * No-op on web, if no token was registered, or if no access token is provided.
 *
 * @param {string} [accessToken] Supabase session access_token
 */
export async function cleanupPushToken(accessToken) {
  if (!Capacitor.isNativePlatform()) return;
  if (!accessToken) return;
  let token;
  try { token = localStorage.getItem('orbit_push_token'); } catch {}
  if (!token) return;
  try {
    await apiFetch('/api/notifications/push-token', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ device_token: token }),
    });
    localStorage.removeItem('orbit_push_token');
  } catch {}
}
